import {
  Box,
  FormControl,
  FormLabel,
  Autocomplete,
  TextField,
  Switch,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useDAL } from '@/contexts/useDAL';

interface GcpEncryptionComponentProps {
  cloudAccountId?: string;
  regionName?: string;
  onChange: (isEncrypted: boolean, encryptionKeyName?: string) => void;
  initialIsEncrypted?: boolean;
  title?: string;
}

export const GcpEncryptionComponent = ({
  cloudAccountId,
  regionName,
  onChange,
  initialIsEncrypted,
  title = 'Encrypt this resource',
}: GcpEncryptionComponentProps) => {
  const [isEncrypted, setIsEncrypted] = useState(!!initialIsEncrypted);
  const [selectedKeyName, setSelectedKeyName] = useState<string | undefined>();

  const dal = useDAL();

  const { isLoading, data: body } = dal.restore.gcpVm.useListEncryptionKeys(
    cloudAccountId,
    regionName
  );

  const handleEncryptionToggle = () => {
    const newIsEncrypted = !isEncrypted;
    setIsEncrypted(newIsEncrypted);
    if (!newIsEncrypted) {
      onChange(newIsEncrypted, undefined);
    } else {
      onChange(newIsEncrypted, selectedKeyName);
    }
  };

  const handleKeyChange = (newKeyName: string | undefined) => {
    if (newKeyName === selectedKeyName) {
      return;
    }

    setSelectedKeyName(newKeyName);
    if (isEncrypted) {
      onChange(true, newKeyName);
    }
  };

  const isDisabled =
    isLoading ||
    !cloudAccountId ||
    !regionName ||
    (body && body.keys?.length === 0);

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Box className='flex justify-between items-center'>
        <Typography className='font-semibold' variant='body1'>
          {title}
        </Typography>
        <Switch
          size='small'
          checked={isEncrypted}
          onChange={handleEncryptionToggle}
          data-testid='gcp-encryption-toggle'
        />
      </Box>
      {isEncrypted && (
        <Box className='mt-[20px]'>
          <FormControl size='small' className='w-full'>
            <FormLabel>Customer-managed encryption key (CMEK)</FormLabel>
            <Box className='flex items-center gap-[36px] mt-[8px]'>
              <Box className='w-1/2'>
                <Autocomplete
                  clearOnBlur
                  size='small'
                  className='w-full'
                  options={body?.keys ?? []}
                  disabled={isDisabled}
                  value={
                    (body?.keys ?? []).find(
                      (k) => k.name === selectedKeyName
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    handleKeyChange(newValue?.name);
                  }}
                  getOptionLabel={(option) => parseGcpKeyName(option.name)}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.name}>
                      {parseGcpKeyName(option.name)}
                    </li>
                  )}
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        placeholder='Select'
                        label=''
                        InputLabelProps={{ shrink: true }}
                      />
                    );
                  }}
                />
              </Box>
            </Box>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

// Helper function to parse GCP key name
const parseGcpKeyName = (fullKeyName: string): string => {
  const parts = fullKeyName.split('/');
  if (
    parts.length >= 8 &&
    parts[0] === 'projects' &&
    parts[2] === 'locations' &&
    parts[4] === 'keyRings' &&
    parts[6] === 'cryptoKeys'
  ) {
    const cryptoKeyName = parts[7];
    const keyRingName = parts[5];
    const locationName = parts[3];
    return `${cryptoKeyName} (Ring: ${keyRingName}, Loc: ${locationName})`;
  }
  return fullKeyName; // Fallback to full name if parsing fails
};
